import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef, useCallback } from "react";
import "./addDepartment.css";
import CheckMark from "../../Assets/Icons/CheckMark.png";
import { useDispatch, useSelector } from "react-redux";
import { createDepartment, getDepartmentSuggestion, updateDepartment } from "../../modules/department/departmentThunk";
import { resetCreatedDepartment, resetDeptSuggestion, resetUpdateDepartment } from "../../modules/department/departmentSlice";
import useValidation from "../../utils/validators";
import ToastPopup from "../../widgets/Toast/toastPopup";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";

const AddDepartment = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalRef = useRef(null);
  const { createdDepartment, createdDepartmentError, departmentSuggestion, departmentSuggestionError, updatedDepartment, updatedDepartmentError } = useSelector((state) => state.department);
  const [departmentName, setDepartmentName] = useState("");
  const [enDepartmentName, setEnDepartmentName] = useState("");
  const [departmentCode, setDepartmentCode] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState("");
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const times = [20, 30, 45, 60, 90];
  const { validateDepartmentSave } = useValidation();
  const timeoutIdRef = useRef(null);
  const [searchInputValue, setSearchInutValue] = useState("");
  const [triggerSuggestion, setTriggerSuggestion] = useState(false);
  useEffect(() => {
    if (updatedDepartmentError) {
      ToastPopup.error(updatedDepartmentError);
    }
    if (updatedDepartment && updatedDepartment.status === "success") {
      props.setSelectedView("doctorSpecialization");
      const depName = updatedDepartment.data.department.departmentName;
      ToastPopup.success(t("departmentUpdated", { depName }));
    }
    dispatch(resetUpdateDepartment());
  }, [updatedDepartment, updatedDepartmentError]);

  useEffect(() => {
    if (createdDepartmentError) {
      ToastPopup.error(createdDepartmentError);
    }
    if (createdDepartment && createdDepartment.status === "success") {
      ToastPopup.success(t("depCreated"));
      props.setSelectedView("doctorSpecialization");
    }
    dispatch(resetCreatedDepartment());
  }, [createdDepartmentError, createdDepartment]);

  useEffect(() => {
    if (departmentSuggestion && departmentSuggestion.data.departments) {
      let departmentData = departmentSuggestion.data.departments;
      setSuggestions(departmentData);
    }
  }, [departmentSuggestionError, departmentSuggestion]);

  useEffect(() => {
    if (isEdit) {
      props.ifActivate(selectedSuggestion);
    }
  }, [isEdit, selectedSuggestion, props]);

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  const fetchDepartmentSuggestions = useCallback(
    (value) => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      timeoutIdRef.current = setTimeout(() => {
        const page = 1;
        const limit = 10;
        const sort = "departmentName";
        const searchFields = "departmentName";
        const search = value;

        dispatch(getDepartmentSuggestion({ page, limit, sort, searchFields, search }));
      }, 200);
    },
    [dispatch],
  );

  const handleInputChange = (e) => {
    setTriggerSuggestion(!triggerSuggestion);
    let value = e.target.value.slice(0, 100).trimStart().replace(/[0-9]/g, "");

    setSearchInutValue(value);

    const formattedName = capitalizeFirstLetter(value);
    setDepartmentName(formattedName);

    const formattedCode = value
      .trim()
      .toUpperCase()
      .replace(/[^A-Z]/g, "");
    setDepartmentCode(formattedCode.substring(0, 4));

    const trimmedValue = value.trim();

    if (trimmedValue === "") {
      setSuggestions([]);
      setSelectedTimes([]);
      dispatch(resetDeptSuggestion());
    } else {
      if (!value.endsWith(" ") || value.split(" ").length > 1) {
        fetchDepartmentSuggestions(trimmedValue);
      }
    }

    const matchedSuggestion = suggestions.find((suggestion) => suggestion.departmentName.toLowerCase() === trimmedValue.toLowerCase());

    if (isEdit && !matchedSuggestion) {
      props.ifActivate("");
      props.setEditDepName("");
      setIsEdit(false);
      setSelectedTimes([]);
    }
  };

  const handleEnDepartmentInputChange = (e) => {
    setTriggerSuggestion(!triggerSuggestion);
    let value = e.target.value.slice(0, 100).trimStart().replace(/[0-9]/g, "");

    setEnDepartmentName(value);
  };

  useEffect(() => {
    const matchedSuggestion = suggestions.find((suggestion) => suggestion.departmentName.toLowerCase().trim() === searchInputValue.toLowerCase().trim());
    if (matchedSuggestion) {
      setSelectedSuggestion(matchedSuggestion);
      const times = matchedSuggestion.duration.map((item) => item.timeInMinutes);
      setSelectedTimes(times);
      setDepartmentName(searchInputValue);
      setDepartmentCode(matchedSuggestion.code);
      setIsEdit(true);
      props.setEditDepName(departmentName);
    }
  }, [suggestions, triggerSuggestion]);

  const handleCodeChange = (e) => {
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;

    let value = input.value.toUpperCase().replace(/[^A-Z&]/g, "");
    const maxLength = 4;
    if (value.length <= maxLength && departmentName) {
      setDepartmentCode(value);
      setTimeout(() => {
        input.setSelectionRange(start, end);
      }, 0);
    }
  };

  useImperativeHandle(ref, () => ({
    saveSpeciality,
  }));

  const saveSpeciality = () => {
    const data = {
      departmentName,
      departmentCode,
      selectedTimes,
    };

    const duration = data.selectedTimes.map((time) => {
      const timeInMinutes = parseInt(time, 10);
      return { timeInMinutes };
    });

    if (isEdit) {
      if (selectedTimes.length === 0) {
        ToastPopup.error(t("pleaseSelectOneTime"));
        return;
      }
      const previousTimes = selectedSuggestion.duration.map((item) => item.timeInMinutes);

      const durationToBeAdded = selectedTimes.filter((time) => !previousTimes.includes(time));
      const durationToBeDeleted = previousTimes.filter((time) => !selectedTimes.includes(time)).map((time) => selectedSuggestion.duration.find((d) => d.timeInMinutes === time)._id);

      if (durationToBeAdded.length > 0 || durationToBeDeleted.length > 0) {
        const updatedData = {
          durationToBeAdded,
          durationToBeDeleted,
        };
        const data = {
          departmentId: selectedSuggestion._id,
          updatedData,
        };
        dispatch(updateDepartment(data));
      } else {
        ToastPopup.error(t("noChanges"));
      }
    } else {
      if (validateDepartmentSave(data)) {
        const sendData = {
          departmentName: { en: departmentName, vi: enDepartmentName },
          code: departmentCode,
          duration,
        };
        dispatch(createDepartment(sendData));
      }
    }
  };
  const handleClear = () => {
    setDepartmentName("");
    setDepartmentCode("");
  };

  const handleTimeClick = (time) => {
    if (departmentName.trim() === "") {
      ToastPopup.error(t("plsEnterDepFirst"));
      return;
    }

    setSelectedTimes((prevTimes) => {
      const newTimes = prevTimes.includes(time) ? prevTimes.filter((t) => t !== time) : [...prevTimes, time];
      return newTimes;
    });
  };

  const capitalizeFirstLetter = (str) => (str ? str[0].toUpperCase() + str.slice(1) : str);

  const handleSuggestionClick = (suggestion) => {
    setSelectedSuggestion(suggestion);
    const times = suggestion.duration.map((item) => item.timeInMinutes);
    setSelectedTimes(times);
    props.setEditDepName(suggestion.departmentName);
    setDepartmentName(suggestion.departmentName);
    setDepartmentCode(suggestion.code);
    setIsEdit(true);
    dispatch(resetDeptSuggestion());
    setSuggestions([]);
  };

  return (
    <div>
      <div className="department-add">
        <div className="input-div">
          <div className="dept-input-box">
            <input type="text" className="department-add-input" value={departmentName} onChange={handleInputChange} placeholder={t("enterSpecialityEn")} />
            {departmentName && (
              <button className="clear-btn" onClick={() => handleClear()}>
                <FaTimes />
              </button>
            )}
          </div>
          {suggestions.length > 0 && departmentName && (
            <ul className="suggestions-list" ref={modalRef}>
              {suggestions.map((suggestion, index) => (
                <li key={index} onClick={() => handleSuggestionClick(suggestion)}>
                  {suggestion.departmentName}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="input-div">
          <div className="dept-input-box">
            <input type="text" className="department-add-input" value={enDepartmentName} onChange={handleEnDepartmentInputChange} placeholder={t("enterSpeciality")} />
            {enDepartmentName && (
              <button className="clear-btn" onClick={() => setEnDepartmentName("")}>
                <FaTimes />
              </button>
            )}
          </div>
          {/* {suggestions.length > 0 && departmentName && (
            <ul className="suggestions-list" ref={modalRef}>
              {suggestions.map((suggestion, index) => (
                <li key={index} onClick={() => handleSuggestionClick(suggestion)}>
                  {suggestion.departmentName}
                </li>
              ))}
            </ul>
          )} */}
        </div>
      </div>
      <div className="code-box">
        <div className="speciality-head code seprate-speciality-head">
          <div className="bottom-div-dp">
            <div className="speciality-name">
              {t("departmentCode")}
              <span className="astrik">*</span>
            </div>
            <div className="input-div dp-code-input">
              <input type="text" className="department-add-input" value={departmentCode} onChange={handleCodeChange} placeholder="- - - -" disabled={!departmentName || isEdit} />
            </div>
          </div>
          <div className="bottom-div-dp">
            <div className="speciality-duration">
              {t("durationInMins")}
              <span className="astrik">*</span>
            </div>
            <div className="department-add-time time-duration-create">
              <div className="time-options ">
                {times.map((time, index) => (
                  <div key={index} className={`time-option ${selectedTimes.includes(time) ? "selected" : ""}`} onClick={() => handleTimeClick(time)}>
                    {time} {t("min")}
                    {selectedTimes.includes(time) && <img src={CheckMark} alt="Selected Icon" className="selected-icon" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default AddDepartment;
